import {
  makeSelectedEvent,
  selectEventCurrency,
  selectSelectedBundles,
  selectSelectedTickets,
  selectVoucherData,
  selectWhichChecksIfThereAreUnbundledAddonsOrTopUpForTheSelectedItems,
} from 'app/pages/TicketSalePage/slice/selectors';
import { ArrowRight } from 'lucide-react';
import { DateTime } from 'luxon';
import React from 'react';
import TagManager from 'react-gtm-module';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '../ui/button';
import { fetchCartData, startCountdown } from 'store/cartSlice';
import { selectCartData } from 'store/cartSlice/selectors';
import { getEventSlug } from 'utils/events';
import { getCartTotal } from 'utils/getItemsTotal';
import { parseCartItemsForGTM } from 'utils/gtmUtils';
import { toastOptions } from 'utils/static/constants';
import { buildPriceToShow, roundUpDecimals } from 'utils/tickets';
import { voucherIsApplicableToSelectedTickets } from 'utils/vouchers';
import { formatMoney } from 'utils/formatMoney';

const ProceedToCheckoutButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  // ----- Store ------
  const {
    uuid,
    selectedEvent: { id: cartEventId, name: cartEventName },
    countdown: { endTime },
    holdToken,
  } = useSelector(selectCartData);
  const selectedEvent = useSelector(makeSelectedEvent);
  const eventCurrency = useSelector(selectEventCurrency);
  const voucher = useSelector(selectVoucherData);
  const {
    status: voucherStatus,
    voucherCode: { code: appliedVoucherCode },
    voucherApplicableTickets,
  } = voucher;
  const selectedTickets = useSelector(selectSelectedTickets);
  const selectedBundles = useSelector(selectSelectedBundles);
  const selectedItemsHaveUnbundledAddonsOrTopUp = useSelector(
    selectWhichChecksIfThereAreUnbundledAddonsOrTopUpForTheSelectedItems,
  );

  // ------ Variables ------
  const disabled = selectedTickets.length === 0 && selectedBundles.length === 0;
  const { cartTotal, taxTotal, feeTotal } = getCartTotal(
    [...selectedTickets, ...selectedBundles],
    selectedEvent,
    voucher,
  );

  let secondsRemaining;
  const cartUUID = cartEventId !== selectedEvent.id ? '' : uuid;

  const handleContinueClick = () => {
    sendGTMEvent();
    updateRemainingSeconds();
    if (cartEventId !== selectedEvent.id && secondsRemaining > 0) {
      notify();
    }
    continueToTheNextStep();
  };

  const sendGTMEvent = () => {
    const tagManagerArgs = {
      dataLayer: {
        event: 'add_to_cart',
        ecommerce: {
          currency: eventCurrency,
          value: roundUpDecimals(cartTotal + taxTotal + feeTotal),
          items: parseCartItemsForGTM(
            [...selectedTickets, ...selectedBundles],
            eventCurrency,
            appliedVoucherCode,
            voucherApplicableTickets,
          ),
        },
      },
    };

    TagManager.dataLayer({
      dataLayer: { event: 'remove_from_cart', ecommerce: null },
    });
    TagManager.dataLayer(tagManagerArgs);
  };

  const updateRemainingSeconds = () => {
    if (endTime) {
      secondsRemaining = Math.round(
        DateTime.fromISO(endTime).diffNow('seconds').seconds,
      );
    } else {
      secondsRemaining = 0;
    }
  };

  const notify = () => {
    const element = (
      <Trans
        i18nKey="registrationPage.cancelPreviousCartMessage"
        values={{
          previousEvent: cartEventName,
          currentEvent: selectedEvent.name,
        }}
        components={{ bold: <strong /> }}
      />
    );
    toast.info(element, toastOptions);
  };

  const cancelCart = () => {
    navigate('/cart/cancel', {
      state: {
        eventId: selectedEvent.id,
        eventName: selectedEvent.name,
        type: selectedEvent.type,
      },
    });
  };

  const continueToTheNextStep = async () => {
    const data = {
      event: selectedEvent,
      selectedTickets,
      selectedBundles,
      voucher:
        voucherStatus === 'valid' &&
        voucherIsApplicableToSelectedTickets(
          voucherApplicableTickets,
          selectedTickets,
        )
          ? voucher
          : '',
      order_id: secondsRemaining > 0 ? cartUUID : '',
      hold_token: holdToken?.token,
      queryParams: {
        preview: searchParams.get('preview'),
      },
    };

    const response = await dispatch(fetchCartData(data));

    if (response.error?.code) {
      cancelCart();
      return;
    }

    // true if the previous cart is expired
    if (
      secondsRemaining <= 0 ||
      data.order_id !== response.payload.serverResponse.uuid
    ) {
      handleCountdownStart();
    }
    navigate(getNextStepLink());
  };

  const handleCountdownStart = () => {
    dispatch(
      startCountdown({
        vendorId: selectedEvent.id_seller,
        eventId: selectedEvent.id,
      }),
    );
  };

  const getNextStepLink = () => {
    let baseLink = `/event/${getEventSlug(
      selectedEvent.name,
      selectedEvent.id,
    )}/buy-tickets`;

    if (selectedItemsHaveUnbundledAddonsOrTopUp) {
      baseLink += '/addons';
    } else {
      baseLink += '/billing-information';
    }

    if (searchParams.get('preview')) {
      baseLink += '?preview=true';
    }

    return baseLink;
  };

  const totalQuantity = selectedTickets.reduce(
    (acc, ticket) => acc + ticket.quantity,
    0,
  );

  return (
    <Button disabled={disabled} onClick={handleContinueClick}>
      {selectedEvent.seatingChart && totalQuantity > 0
        ? t(
            totalQuantity === 1 ? 'ticketSale.buySeat' : 'ticketSale.buySeats',
            {
              numberOfSeats: totalQuantity,
              price: buildPriceToShow(
                cartTotal + taxTotal + feeTotal,
                eventCurrency,
                formatMoney(0, eventCurrency),
              ),
            },
          )
        : t('common.continue')}
      <ArrowRight size={24} />
    </Button>
  );
};

export default ProceedToCheckoutButton;
